/**
 * WalletModal.tsx
 * ─────────────────────────────────────────────────────────────
 * Wallet connect button with two usage modes:
 *
 * MODE A — RainbowKit (TokenCreator, top-bar):
 *   <WalletModal compact />
 *   Uses wagmi/RainbowKit hooks internally. No props needed.
 *
 * MODE B — Legacy callback (IdentityMint):
 *   <WalletModal onConnect={connect} isConnecting={isConnecting} />
 *   Calls your existing useMonadWallet connect fn, but renders
 *   a multi-wallet button that opens RainbowKit modal first,
 *   then your hook takes over once the address is available.
 *
 * REQUIRES (in main.tsx):
 *   import '@rainbow-me/rainbowkit/styles.css'
 *   Wrap app with <WagmiProvider> + <RainbowKitProvider>
 * ─────────────────────────────────────────────────────────────
 */

import { ConnectButton } from "@rainbow-me/rainbowkit";
import { Loader2, Wallet } from "lucide-react";

interface WalletModalProps {
  /** Compact pill variant for top-bar use (TokenCreator) */
  compact?: boolean;
  /** Legacy: pass useMonadWallet's connect fn (IdentityMint) */
  onConnect?: () => void;
  /** Legacy: pass useMonadWallet's isConnecting state (IdentityMint) */
  isConnecting?: boolean;
}

export default function WalletModal({ compact = false, onConnect, isConnecting = false }: WalletModalProps) {
  return (
    <ConnectButton.Custom>
      {({
        account,
        chain,
        openAccountModal,
        openChainModal,
        openConnectModal,
        authenticationStatus,
        mounted,
      }) => {
        const ready = mounted && authenticationStatus !== "loading";
        const connected =
          ready &&
          account &&
          chain &&
          (!authenticationStatus || authenticationStatus === "authenticated");

        if (!ready) return null;

        // ── Not connected ──
        if (!connected) {
          // In legacy mode (IdentityMint), open RainbowKit first —
          // useMonadWallet's connect will pick up the injected provider after
          const handleClick = () => {
            openConnectModal();
            // If a legacy callback is provided, call it after a tick so
            // RainbowKit has time to inject the wallet
            if (onConnect) setTimeout(onConnect, 500);
          };

          return compact ? (
            <button
              onClick={handleClick}
              className="btn-pink py-2 px-4 flex items-center gap-2 text-sm"
            >
              <Wallet size={14} />
              Connect Wallet
            </button>
          ) : (
            <button
              onClick={handleClick}
              disabled={isConnecting}
              className="btn-pink w-full flex items-center justify-center gap-2 py-3.5 text-sm font-mono"
            >
              {isConnecting ? (
                <><Loader2 size={16} className="animate-spin" /> Connecting…</>
              ) : (
                <><Wallet size={16} /> Connect Wallet</>
              )}
            </button>
          );
        }

        // ── Wrong network ──
        if (chain.unsupported) {
          return (
            <button
              onClick={openChainModal}
              className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-xl px-3 py-2 text-xs text-red-400 font-mono hover:bg-red-500/20 transition-colors"
            >
              <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
              Wrong network — switch
            </button>
          );
        }

        // ── Connected: compact pill (TokenCreator top-bar) ──
        if (compact) {
          return (
            <div className="flex items-center gap-2">
              <button
                onClick={openChainModal}
                className="flex items-center gap-1.5 bg-white/5 border border-[#3d0033] rounded-lg px-2 py-1.5 text-[10px] font-mono text-white/50 hover:text-white/80 transition-colors"
              >
                {chain.hasIcon && chain.iconUrl && (
                  <img src={chain.iconUrl} alt={chain.name} className="w-3 h-3 rounded-full" />
                )}
                {chain.name}
              </button>
              <button
                onClick={openAccountModal}
                className="flex items-center gap-2 bg-[#ff2680]/10 border border-[#ff2680]/30 rounded-xl px-3 py-2 hover:bg-[#ff2680]/20 transition-colors"
              >
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
                <span className="font-mono text-xs text-[#ff2680] font-bold truncate max-w-[120px]">
                  {account.displayName}
                </span>
                {account.displayBalance && (
                  <span className="text-[10px] text-white/30 font-mono hidden sm:inline">
                    {account.displayBalance}
                  </span>
                )}
              </button>
            </div>
          );
        }

        // ── Connected: full-width (not used post-connect in IdentityMint,
        //    which renders its own address row — but kept for completeness) ──
        return (
          <div className="flex items-center gap-3 p-3 rounded-xl bg-[#1a0016] border border-[#3d0033]">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shrink-0" />
            <span className="font-mono text-xs text-white/70 truncate flex-1">
              {account.address}
            </span>
            <button
              onClick={openAccountModal}
              className="text-[10px] text-[#ff2680]/50 hover:text-[#ff2680] font-mono uppercase transition-colors shrink-0"
            >
              {account.displayName}
            </button>
          </div>
        );
      }}
    </ConnectButton.Custom>
  );
}